import { SelectionModel } from '@angular/cdk/collections';
import {
  Component,
  HostBinding,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { UserOrganizationClient } from '@app-services/api/clients/user-organization.client';
import {
  BaseGetByFileIdRequest,
  BaseGetByIdRequest,
  BaseGetSearchCountByIdRequest,
} from '@app-types/base/base';
import { RoleType } from '@app-types/enums/role-type';
import { UserOrganizationGetOrganizationUsersContract } from '@app-types/api/user-organization';
import { UserOrganizationState } from '@app-types/enums/user-organization-state.enum';
import { MobileObserverService } from '@app-services/adaptive/mobile-observer.service';
import { PermissionService } from '@app-services/permission/permission.service';
import { ConfirmDialogComponent } from '@app-components/common/confirm-dialog/confirm-dialog.component';
import { MatchError } from 'src/app/services/errors/error-matcher';
import { OrganizationSettingsClient } from 'src/app/services/api/clients/organization-settings.client';
import { SomethingWentWrongComponent } from '@app-components/common/error/something-went-wrong/something-went-wrong.component';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatCardModule } from '@angular/material/card';
import { NoDataComponent } from '@app-components/common/no-data/no-data.component';
import { SearchBarComponent } from '@app-components/common/search-bar/search-bar.component';
import { DrawerService } from '@app-services/drawer.service';
import {
  SETTING_SETTINGS,
  defaultFilter,
} from '@app-shared/constants/constants';
import { OrganizationUserCreateDialogComponent } from '@app-components/settings/organization/organization-users/organization-user-create-dialog/organization-user-create-dialog.component';
import { AddUsersToGroupsFormComponent } from '@app-components/settings/organization/organization-users/add-users-to-groups-form/add-users-to-groups-form.component';
import { FormatUserStatePipe } from '@app-pipes/format-user-state.pipe';
import { AddUsersToMailAccountsFormComponent } from '@app-components/settings/organization/organization-users/add-users-to-mail-accounts-form/add-users-to-mail-accounts-form.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subscription } from 'rxjs';
import { SkeletonTableComponent } from '@app-components/common/skeletons/skeleton-table/skeleton-table.component';
import { TableHeaderComponent } from '@app-components/common/table-header/table-header.component';
import { InfinityScrollComponent } from '@app-components/common/infinity-scroll/infinity-scroll.component';
import { UserWorkspaceService } from '@app-services/user-workspace-service';
import { FilterFields, OptionsEnum } from '@app-types/common';
import { UserOrganizationSearchOrder } from '@app-types/enums/UserOrganizationSearchOrder';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { OrderDirection } from '@app-types/enums/order-direction';
import { FormatUsersSearchOrders } from '@app-pipes/format-users-search-orders.pipe';
import { LoaderComponent } from '@app-components/common/loader/loader.component';
import { TempFileClient } from '@app-services/api/clients/temp-file.client';

const { PAGE_SIZE, STEP_FETCH } = SETTING_SETTINGS;

@Component({
  selector: 'app-organization-users',
  templateUrl: './organization-users.component.html',
  styleUrls: ['./organization-users.component.scss'],
  imports: [
    SomethingWentWrongComponent,
    TranslateModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatCardModule,
    NoDataComponent,
    MatTableModule,
    SearchBarComponent,
    OrganizationUserCreateDialogComponent,
    AddUsersToGroupsFormComponent,
    FormatUserStatePipe,
    AddUsersToMailAccountsFormComponent,
    MatTooltipModule,
    SkeletonTableComponent,
    TableHeaderComponent,
    InfinityScrollComponent,
    MatSort,
    MatSortModule,
    LoaderComponent,
  ],
})
export class OrganizationUsersComponent implements OnInit, OnDestroy {
  @HostBinding('class') className = 'setting-container';
  public organizationId: number | undefined;
  public dataSource =
    new MatTableDataSource<UserOrganizationGetOrganizationUsersContract>([]);
  public displayedColumns: string[] = [
    'select',
    'firstName',
    'lastName',
    'oidcIdentity',
    'adfsIdentity',
    'email',
    'role',
    'state',
    'actions',
  ];
  public isLoading = true;
  public isError = false;
  public iscredemISAMSupport = true;
  public roleTypes = RoleType;
  public totalSize = 0;

  public searchValue = '';
  public useMobileView = false;
  public canNavigateForward = false;
  public isLoadingScroll = false;
  public isErrorScroll = false;
  public offset = 0;
  public stepFetch = STEP_FETCH;
  public pageSize = PAGE_SIZE;
  public filter: FilterFields<UserOrganizationSearchOrder> = {
    ...defaultFilter,
  };
  public searchOrder: UserOrganizationSearchOrder | 0 = 0;
  public searchOrders: OptionsEnum[];
  private formatUsersSearchOrders = new FormatUsersSearchOrders();
  public isExporting = false;
  isReloadLoading = false;
  private suppressSortDrivenSearch = false;
  public selection =
    new SelectionModel<UserOrganizationGetOrganizationUsersContract>(true, []);
  selectedUser: UserOrganizationGetOrganizationUsersContract | null;
  private useMobileViewSubscription: Subscription;
  @ViewChild('createDrawer') createDrawer: TemplateRef<any>;
  @ViewChild('addUsersToGroupsDrawer') addUsersToGroupsDrawer: TemplateRef<any>;
  @ViewChild('addUsersToMailAccountsDrawer')
  addUsersToMailAccountsDrawer: TemplateRef<any>;
  @ViewChild(InfinityScrollComponent) infinityScroll: InfinityScrollComponent;
  @ViewChild(MatSort) sort: MatSort;

  oidcFederatedSupport = false;

  constructor(
    private userOrganizationClient: UserOrganizationClient,
    private permissionService: PermissionService,
    private matchError: MatchError,
    private organizationSettingsClient: OrganizationSettingsClient,
    public dialog: MatDialog,
    private mobileObserverService: MobileObserverService,
    private drawerService: DrawerService,
    protected userWorkspaceService: UserWorkspaceService,
    private tempFileClient: TempFileClient
  ) {}

  async ngOnInit(): Promise<void> {
    this.useMobileViewSubscription = this.mobileObserverService
      .mobileObserver()
      .subscribe(isMobile => (this.useMobileView = isMobile));

    this.organizationId =
      this.userWorkspaceService.currentOrganization?.organizationId;

    this.organizationId &&
      (await this.getOrganisationSettings(this.organizationId));

    this.searchOrders = this.displayedColumns
      .filter(el => el !== 'actions' && el !== 'select')
      .map(key => ({
        id: this.formatUsersSearchOrders.transform(key),
        name: key,
      }));

    await this.loadOrganizationUsers(true);
  }

  async getOrganisationSettings(organizationId: number): Promise<void> {
    const organizationSettingsResponse =
      await this.organizationSettingsClient.getByOrganization(
        new BaseGetByIdRequest(organizationId)
      );
    this.oidcFederatedSupport =
      organizationSettingsResponse.result.oidcFederatedSupport;
  }

  public async changeState(
    userOrganizationId: number,
    state: UserOrganizationState
  ): Promise<void> {
    try {
      if (state === UserOrganizationState.Disabled) {
        await this.userOrganizationClient.changeState({
          userOrganizationId,
          state: UserOrganizationState.Active,
        });
      } else {
        await this.userOrganizationClient.changeState({
          userOrganizationId,
          state: UserOrganizationState.Disabled,
        });
      }
      await this.loadOrganizationUsers(false);
    } catch (e) {
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    }
  }

  public openConfirmDialog(
    userOrganizationId: number,
    state: UserOrganizationState,
    user: UserOrganizationGetOrganizationUsersContract
  ): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '450px',
      autoFocus: false,
      data: {
        name: 'stateChange',
        isDontNeedConfirmWord: true,
        title:
          state === UserOrganizationState.Active
            ? 'stateActiveTitle'
            : 'stateDisabledTitle',
        itemName: `${user.firstName} ${user.lastName}`,
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.changeState(userOrganizationId, state);
      }
    });
  }

  public async loadOrganizationUsers(
    fetchSearchCounter: boolean
  ): Promise<void> {
    if (!this.organizationId) {
      return;
    }
    this.canNavigateForward = false;
    const loaderTimeout =
      !this.offset && !this.dataSource.data.length
        ? setTimeout(() => {
            this.isLoading = true;
          }, 500)
        : undefined;

    this.offset && (this.isLoadingScroll = true);
    this.isError = false;
    this.isErrorScroll = false;

    try {
      const request = {
        organizationId: this.organizationId,
        offset: this.offset,
        fetch: this.stepFetch,
        filter: this.filter,
        search: this.searchValue ? this.searchValue : null,
      };
      const organizationSettings =
        await this.organizationSettingsClient.getByOrganization(
          new BaseGetByIdRequest(this.organizationId)
        );
      this.iscredemISAMSupport = organizationSettings.result.credemISAMSupport;

      const response = await this.userOrganizationClient.search(request);

      let organizationUsers = response.data.filter(e =>
        this.permissionService.hasPermissionToSeeUserByUserEmail(e.email)
      );

      organizationUsers = organizationUsers.filter(e => e.roleId !== 1);

      this.dataSource.data =
        this.offset === 0
          ? organizationUsers
          : [...this.dataSource.data, ...organizationUsers];

      if (fetchSearchCounter) {
        const countRequest = new BaseGetSearchCountByIdRequest(
          this.organizationId,
          this.searchValue ? this.searchValue : null
        );
        const counterResponse =
          await this.userOrganizationClient.getSearchCount(countRequest);
        this.totalSize = counterResponse.result;
      }
      const countHidden = response.data.length - organizationUsers.length;
      this.totalSize = this.totalSize - countHidden;
      this.canNavigateForward = this.totalSize !== this.dataSource.data.length;
    } catch (e) {
      if (this.isLoadingScroll) {
        this.isErrorScroll = true;
      } else {
        this.isError = true;
      }
      this.matchError.logError(e);
    } finally {
      loaderTimeout && clearTimeout(loaderTimeout);
      this.isLoading && (this.isLoading = false);
      this.isLoadingScroll && (this.isLoadingScroll = false);
    }
  }

  public onSearchValueChanged(newSearchValue: string): void {
    this.searchValue = newSearchValue;
  }

  async onCreateOrganizationUserBtnClicked(): Promise<void> {
    this.drawerService.openDrawer(this.createDrawer);
  }

  async onUpdateOrganizationUserBtnClicked(
    user: UserOrganizationGetOrganizationUsersContract
  ): Promise<void> {
    this.selectedUser = user;
    this.drawerService.openDrawer(this.createDrawer);
  }

  onCloseCreateModalForm(): void {
    this.selectedUser && (this.selectedUser = null);
  }

  async onCreateUpdateOrganizationUser(): Promise<void> {
    await this.onSearch(true);
  }

  public isRta(organizationUserRole: RoleType): boolean {
    return organizationUserRole === RoleType.Rta;
  }

  isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle(): void {
    this.isAllSelected()
      ? this.selection.clear()
      : this.dataSource.data.forEach(row => this.selection.select(row));
  }

  public onAddUserToGroupsBtnClicked(
    user?: UserOrganizationGetOrganizationUsersContract
  ): void {
    user && (this.selectedUser = user);
    this.drawerService.openDrawer(this.addUsersToGroupsDrawer);
  }

  public onHideAddUsersToGroupForm(): void {
    this.selectedUser && (this.selectedUser = null);
  }

  public onAddUsersToGroups(): void {
    this.selectedUser && (this.selectedUser = null);
    this.selection.selected && this.selection.clear();
  }

  public onAddUserToMailAccountsBtnClicked(
    user: UserOrganizationGetOrganizationUsersContract
  ): void {
    this.selectedUser = user;
    this.drawerService.openDrawer(this.addUsersToMailAccountsDrawer);
  }

  public onHideAddUsersToMailAccountsForm(): void {
    this.selectedUser && (this.selectedUser = null);
  }

  public countTo(): string {
    return this.dataSource.data.length.toString();
  }

  public async onScroll(): Promise<void> {
    this.offset = this.offset + this.pageSize;
    await this.loadOrganizationUsers(false);
  }

  async refreshData(): Promise<void> {
    await this.loadOrganizationUsers(false);
  }

  resetScrollData(): void {
    this.offset = 0;
  }

  clearSort(): void {
    if (!this.sort) {
      return;
    }
    this.sort.sort({ id: '', start: 'asc', disableClear: false });
    this.sort.active = '';
    this.sort.direction = '';
  }

  async reload(): Promise<void> {
    this.isReloadLoading = true;
    this.selection.clear();
    this.filter = { ...defaultFilter };
    this.searchOrder = 0;
    this.suppressSortDrivenSearch = true;
    this.clearSort();
    try {
      await this.onSearch();
    } finally {
      this.isReloadLoading = false;
      setTimeout(() => {
        this.suppressSortDrivenSearch = false;
      }, 0);
    }
  }

  async onSearch(fetchSearchCounter = true): Promise<void> {
    this.resetScrollData();
    this.infinityScroll?.scrollTop();
    await this.loadOrganizationUsers(fetchSearchCounter);
  }

  async sortData(sort: Sort): Promise<void> {
    if (this.suppressSortDrivenSearch) {
      return;
    }
    this.filter.searchOrder = sort.direction
      ? this.formatUsersSearchOrders.transform(sort.active)
      : UserOrganizationSearchOrder.OrderByUserOrganizationId;

    this.filter.orderDirection =
      sort.direction === 'desc'
        ? OrderDirection.Descending
        : OrderDirection.Ascending;

    await this.onSearch();
  }

  async changeOrder(): Promise<void> {
    this.filter.orderDirection =
      this.filter.orderDirection === OrderDirection.Ascending
        ? OrderDirection.Descending
        : OrderDirection.Ascending;
    await this.onSearch();
  }

  public async searchOrderChange($event: any): Promise<void> {
    this.searchOrder = $event as UserOrganizationSearchOrder;
    this.filter.searchOrder = this.searchOrder;
    this.filter.orderDirection = OrderDirection.Ascending;
    await this.onSearch();
  }

  async exportExcel(): Promise<void> {
    if (!this.organizationId) return;
    this.isExporting = true;
    try {
      const fileResponse = await this.userOrganizationClient.exportExcel();
      const downloadRequest = new BaseGetByFileIdRequest(
        fileResponse.result.fileId
      );
      await this.tempFileClient.download(downloadRequest);
    } catch (e) {
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    } finally {
      this.isExporting = false;
    }
  }

  async retryData(): Promise<void> {
    await this.loadOrganizationUsers(true);
    setTimeout(() => {
      this.infinityScroll?.reconnect();
    }, 1000);
  }

  ngOnDestroy(): void {
    this.useMobileViewSubscription?.unsubscribe();
  }
}
